import { IApplicationFull, IApplicationFullResult, IGroupedComponentFull } from '../types';
import { uniq } from './array';

function getResults(
  refIds: number[] | undefined,
  applicationFull: IApplicationFull | undefined,
  refType: 'parent' | 'scope' | 'component'
) {
  if (typeof refIds === 'undefined' || typeof applicationFull === 'undefined') {
    return undefined;
  }
  return Object.values(applicationFull).reduce<IApplicationFullResult[]>((acc, results) => {
    const mainResut = getMainApplicationFullResult(results as IApplicationFullResult[]);
    const refId = mainResut ? mainResut[`${refType}RefId`] : undefined;
    if (refId === null || refId === undefined) {
      return acc;
    }
    if (refIds.includes(refId)) {
      return [...acc, ...(results as IApplicationFullResult[])];
    }
    return acc;
  }, []);
}

export function getApplicationFullResults(applicationFull: IApplicationFull | undefined): IApplicationFullResult[] {
  return Object.values(applicationFull || {}).reduce<IApplicationFullResult[]>(
    (acc, results) => [
      ...acc,
      ...((results as IApplicationFullResult[])?.length && (results as IApplicationFullResult[])?.at(0)?.key
        ? (results as IApplicationFullResult[])
        : []),
    ],
    []
  );
}
/**
 * Get list of IApplicationFullResult corresponding to the parentRefs
 * @param parentRefIds List of parentRef id
 * @param applicationFull ApplicationFull object
 * @returns List of IApplicationFullResult
 */
export function getParentsResults(
  parentRefIds: number[] | undefined,
  applicationFull: IApplicationFull | undefined
): IApplicationFullResult[] | undefined {
  return getResults(parentRefIds, applicationFull, 'parent');
}

export function getTargettedRecordStatus(
  currentResult: IApplicationFull | undefined,
  costComponentRefsIds: number[] | undefined
): IApplicationFullResult[] | undefined {
  return currentResult
    ? Object.values(currentResult as IApplicationFull).reduce((acc: Array<IApplicationFullResult>, curr) => {
        if (
          curr &&
          Array.isArray(curr) &&
          curr.length > 0 &&
          typeof curr[0] === 'object' &&
          costComponentRefsIds?.includes(curr[0].componentRefId as number) &&
          curr[0].isMainResult === true
        ) {
          acc.push(curr[0]);
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return acc as any;
      }, [])
    : undefined;
}

/**
 * Get list of IApplicationFullResult corresponding to the scopeRefs
 * @param scopeRefIds List of scopeRef id
 * @param applicationFull ApplicationFull object
 * @returns List of IApplicationFullResult
 */
export function getScopesResults(
  scopeRefIds: number[] | undefined,
  applicationFull: IApplicationFull | undefined
): IApplicationFullResult[] | undefined {
  return getResults(scopeRefIds, applicationFull, 'scope');
}

/**
 * Get list of IApplicationFullResult corresponding to the componentRefs
 * @param componentRefIds List of componentRef id
 * @param applicationFull ApplicationFull object
 * @returns List of IApplicationFullResult
 */
export function getComponentsResults(
  componentRefIds: number[] | undefined,
  applicationFull: IApplicationFull | undefined
): IApplicationFullResult[] | undefined {
  return getResults(componentRefIds, applicationFull, 'component');
}

export function getApplicationFullRoadmapIds(applicationFull: IApplicationFull | undefined): number[] | undefined {
  return applicationFull?.roadmapIds;
}

export function getApplicationFullRecommendationIds(
  applicationFull: IApplicationFull | undefined
): number[] | undefined {
  return applicationFull
    ? uniq(
        Object.values(applicationFull).reduce<number[]>((acc, value) => {
          const recommendationIds: number[] | undefined = getMainApplicationFullResult(
            value as IApplicationFullResult[]
          )?.recommendationIds;
          return recommendationIds ? [...acc, ...recommendationIds] : acc;
        }, [])
      )
    : undefined;
}

/**
 * Get the applicationFullResult that is not versionned
 * @param applicationFullResults List of applicationFullResult
 * @returns The applicationFullResult that is not versionned or null
 */
export function getMainApplicationFullResult(
  applicationFullResults: IApplicationFullResult[] | undefined
): IApplicationFullResult | undefined {
  if (!applicationFullResults || !applicationFullResults.length || typeof applicationFullResults === 'string') {
    return undefined;
  }

  return applicationFullResults.find((result) => result?.isMainResult) || applicationFullResults[0];
}

export function getApplicationFullResult(
  applicationFullResults: IApplicationFullResult[] | undefined
): IApplicationFullResult[] | undefined {
  if (!applicationFullResults || !applicationFullResults.length || typeof applicationFullResults === 'string') {
    return undefined;
  }
  const filteredResults = applicationFullResults.filter((applicationFullResult) => !applicationFullResult.version);
  return filteredResults.sort((a, b) => Number(b.isMainResult) - Number(a.isMainResult));
}

export const hasSomeNameFull = (groupedComponents: IGroupedComponentFull[]): boolean => {
  const names = groupedComponents
    .flatMap((groupedComponent) => groupedComponent.componentsAggrByType)
    .flatMap((componentAggrByType) => componentAggrByType.groupedResultsByVersion)
    .flatMap((groupedResultsByVersion) => groupedResultsByVersion.groupedResultsByName)
    .flatMap((groupedResult) => groupedResult.results)
    .flatMap((result) => result?.name);

  return names.every((name) => name === null || name === undefined) ? false : true;
};

export const hasOnlyGoodResult = (groupedComponents: IGroupedComponentFull[]): boolean => {
  const rules = groupedComponents
    .flatMap((groupedComponent) => groupedComponent.componentsAggrByType)
    .flatMap((componentAggrByType) => componentAggrByType.groupedResultsByVersion)
    .flatMap((groupedResultsByVersion) => groupedResultsByVersion.groupedResultsByName)
    .flatMap((groupedResult) => groupedResult.results)
    .flatMap((result) => result?.rule);

  return rules.every((rule) => rule === 'good' || rule === null || rule === 'n/d' || rule === 'n/a') ? true : false;
};
